/** Resource helpers shared by pages: kind labels, names and tags, grouping, search, status resource. */
import type { AssignmentDto, ResourceDto, TaskDto } from '../../shared/api.js';
import { isDone } from '../../shared/deadline.js';
import type { DirectoryAccountStatus, ResourceKind } from '../../shared/model.js';
import { resourceLabel, type LabelledResource } from '../../shared/resourceLabel.js';
import type { LocalMinutes } from '../../shared/time.js';

export const KIND_ORDER: readonly ResourceKind[] = ['person', 'hired', 'equipment', 'external', 'placeholder', 'status'];

export const KIND_LABEL: Record<ResourceKind, string> = {
  person: 'Medewerkers',
  hired: 'Inhuur',
  equipment: 'Materieel',
  external: 'Extern',
  placeholder: 'Overig',
  status: 'Status',
};

/** Singular label for a badge/select. */
export const KIND_LABEL_SINGULAR: Record<ResourceKind, string> = {
  person: 'Medewerker',
  hired: 'Inhuur',
  equipment: 'Materieel',
  external: 'Extern',
  placeholder: 'Overig',
  status: 'Statuslabel',
};

/** Lower-case, accents removed: for forgiving search ("Sélène" matches "selene"). */
export function normalizeSearch(s: string): string {
  return s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().trim();
}

export function matchesSearch(haystack: string, query: string): boolean {
  const q = normalizeSearch(query);
  if (!q) return true;
  const h = normalizeSearch(haystack);
  return q.split(/\s+/).every((part) => h.includes(part));
}

/** Text a resource is found by: the name shown (the Microsoft name of a linked person) and the ProjectLibre name. */
export function resourceSearchText(r: LabelledResource): string {
  const label = resourceLabel(r);
  return label === r.name ? label : `${label} ${r.name}`;
}

/** The ProjectLibre name when TransectLibre shows another one ("In ProjectLibre: Sanne3"), else null. */
export function projectLibreName(r: LabelledResource): string | null {
  return resourceLabel(r) === r.name ? null : r.name;
}

/** Tooltip for a resource name: the ProjectLibre name when it differs from the name shown. */
export function resourceNameTitle(r: LabelledResource): string {
  const pl = projectLibreName(r);
  return pl ? `${resourceLabel(r)} (in ProjectLibre: ${pl})` : resourceLabel(r);
}

/** Sorts by the name shown (Dutch collation, case-insensitive), then by uid. */
export function byResourceLabel<R extends LabelledResource & { uid: number }>(a: R, b: R): number {
  return resourceLabel(a).localeCompare(resourceLabel(b), 'nl', { sensitivity: 'base' }) || a.uid - b.uid;
}

/** Label shown next to a name: an external person, or a colleague without a Microsoft account. */
export type ResourceTagKind = 'external' | 'no-account';

export const RESOURCE_TAG_LABEL: Record<ResourceTagKind, string> = {
  external: 'Extern',
  'no-account': 'Geen account',
};

export const RESOURCE_TAG_TITLE: Record<ResourceTagKind, string> = {
  external: 'Externe persoon (geen medewerker van Transect)',
  'no-account': 'Medewerker zonder Microsoft-account',
};

export function resourceTag(r: Pick<ResourceDto, 'kind' | 'noAccount'>): ResourceTagKind | null {
  if (r.kind === 'external') return 'external';
  if (r.kind === 'person' && r.noAccount === true) return 'no-account';
  return null;
}

/** Why the daily Microsoft 365 check made someone inactive (Resource.inactiveSource.status). */
export const DIRECTORY_STATUS_LABEL: Record<DirectoryAccountStatus, string> = {
  disabled: 'Microsoft-account uitgeschakeld',
  'not-licensed': 'geen Microsoft 365-licentie meer',
  'not-found': 'Microsoft-account verwijderd',
};

/** "Sinds de controle van 29-10: Microsoft-account uitgeschakeld", or null when not made inactive by the check. */
export function inactiveReason(r: Pick<ResourceDto, 'isInactive' | 'inactiveSource'>): string | null {
  const source = r.isInactive ? r.inactiveSource : null;
  if (!source || source.by !== 'directory') return null;
  return `Sinds de controle van ${source.since.slice(8, 10)}-${source.since.slice(5, 7)}: ${DIRECTORY_STATUS_LABEL[source.status] ?? 'Microsoft-account niet in orde'}`;
}

export interface PlannedWorkSource {
  tasks: ReadonlyMap<number, TaskDto>;
  parent: ReadonlyMap<number, number | null>;
  assignments: ReadonlyMap<number, AssignmentDto>;
  assignmentsByResource: ReadonlyMap<number, readonly number[]>;
}

/** The task or one of its ancestors (a phase or project) is marked afgerond. */
function doneOrInDonePhase(src: Pick<PlannedWorkSource, 'tasks' | 'parent'>, t: TaskDto): boolean {
  for (let cur: TaskDto | undefined = t; cur; ) {
    if (isDone(cur)) return true;
    const p = src.parent.get(cur.uid);
    cur = p === null || p === undefined ? undefined : src.tasks.get(p);
  }
  return false;
}

/**
 * Tasks a resource is assigned to that finish after `now` (the assignment's own dates first),
 * soonest first: what still has to be reassigned when someone leaves. Work marked afgerond
 * (the task or a phase above it) is finished and not listed.
 */
export function plannedTasksAfter(src: PlannedWorkSource, resourceUid: number, now: LocalMinutes): TaskDto[] {
  const out = new Map<number, { task: TaskDto; start: number }>();
  for (const aUid of src.assignmentsByResource.get(resourceUid) ?? []) {
    const a = src.assignments.get(aUid);
    const t = a ? src.tasks.get(a.taskUid) : undefined;
    if (!a || !t || t.isNull || doneOrInDonePhase(src, t)) continue;
    const finish = a.finish ?? t.finish;
    if (finish === null || finish <= now) continue;
    out.set(t.uid, { task: t, start: a.start ?? t.start ?? finish });
  }
  return [...out.values()].sort((x, y) => x.start - y.start || x.task.uid - y.task.uid).map((x) => x.task);
}

export interface ResourceGroup<R extends Pick<ResourceDto, 'kind'>> {
  kind: ResourceKind;
  label: string;
  resources: R[];
}

/** Groups resources by kind in KIND_ORDER, keeping the given order inside each group; empty groups are dropped. */
export function groupByKind<R extends Pick<ResourceDto, 'kind'>>(resources: readonly R[]): ResourceGroup<R>[] {
  return KIND_ORDER.map((kind) => ({ kind, label: KIND_LABEL[kind], resources: resources.filter((r) => r.kind === kind) })).filter(
    (g) => g.resources.length > 0,
  );
}

/** Marker in the resource list: works without a Microsoft account, or (admins only) a person still to link. */
export type AccountMarker = 'no-account' | 'unlinked' | null;

export const ACCOUNT_MARKER_LABEL: Record<Exclude<AccountMarker, null>, string> = {
  'no-account': RESOURCE_TAG_LABEL['no-account'],
  unlinked: 'Niet gekoppeld',
};

/**
 * 'unlinked' only for the resources the admin's account suggestions cover: active, non-generic
 * people without a link and not marked as working without an account.
 */
export function accountMarker(r: Pick<ResourceDto, 'kind' | 'userOid' | 'noAccount' | 'isInactive' | 'isGeneric'>, isAdmin: boolean): AccountMarker {
  if (r.noAccount === true) return 'no-account';
  if (isAdmin && r.kind === 'person' && !r.userOid && !r.isInactive && !r.isGeneric) return 'unlinked';
  return null;
}

const TENTATIVE_NAME = 'planning niet definitief';

/**
 * The status resource that marks a task "niet definitief": the 'status' resource
 * named "PLANNING NIET DEFINITIEF" (exact, case-insensitive), else any status
 * resource whose name contains "niet definitief".
 */
export function findTentativeResource<R extends Pick<ResourceDto, 'kind' | 'name'>>(resources: Iterable<R>): R | null {
  let fallback: R | null = null;
  for (const r of resources) {
    if (r.kind !== 'status') continue;
    const name = normalizeSearch(r.name);
    if (name === TENTATIVE_NAME) return r;
    if (!fallback && name.includes('niet definitief')) fallback = r;
  }
  return fallback;
}

/** Initials for an avatar: "Marieke de Vries" -> "MV", "dGPS3" -> "DG". */
export function initialsOf(name: string): string {
  const words = name
    .replace(/[()[\],.]/g, ' ')
    .split(/\s+/)
    .filter(Boolean)
    .filter((w, i, all) => i === 0 || i === all.length - 1 || !/^(de|van|der|den|het|ten|ter|te|op|in|'t)$/i.test(w));
  if (words.length === 0) return '?';
  if (words.length === 1) return words[0]!.slice(0, 2).toUpperCase();
  return (words[0]![0]! + words[words.length - 1]![0]!).toUpperCase();
}
